// ============================================================================
// Vista — Variantes (panel-cliente).
// ============================================================================

import { crearTabla, mostrarToast, iconoCandado } from '@plataforma/ui';
import { escapeAttr, mensajeErrorAmigable, tieneFeature } from '@plataforma/utils';

import { obtenerProducto } from '../api/productos.js';
import {
  listarAtributos,
  crearAtributo,
  actualizarAtributo,
  eliminarAtributo,
  listarValores,
  crearValor,
  actualizarValor,
  eliminarValor,
  listarVariantesProducto,
  crearVariante,
  actualizarVariante,
  eliminarVariante,
  asignarValoresVariante,
  eliminarValoresVariante,
  generarCombinacionesVariantes,
} from '../api/variantes.js';
import {
  aplicarEstadoBorrador,
  conectarBorradorDeFormulario,
  crearGestorBorradorFormulario,
} from '../borradores.js';
import { obtenerEstadoSesion } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

function leerQueryParam(param) {
  const hash = window.location.hash.split('?')[1] ?? '';
  const params = new URLSearchParams(hash);
  return params.get(param);
}

/**
 * Conecta el borrador de un formulario que vive dentro de un <pf-modal>.
 * Extraído para no repetir la misma secuencia en los cuatro modales de esta
 * vista (atributo, valores, editar valor, variante).
 *
 * @param {object} opciones
 * @param {HTMLElement} opciones.modal
 * @param {string} opciones.ambito
 * @param {string} opciones.contexto
 * @param {() => object} opciones.leerDatos
 * @param {(datos: object) => void} opciones.aplicarDatos
 * @param {string} opciones.sufijo - prefijo de los ids de aviso/estado.
 * @returns {object} el objeto devuelto por conectarBorradorDeFormulario.
 */
function conectarBorradorModal({ modal, ambito, contexto, leerDatos, aplicarDatos, sufijo }) {
  const gestor = crearGestorBorradorFormulario(ambito, {
    contexto,
    alEstado: (estado) =>
      aplicarEstadoBorrador(modal.querySelector(`#${sufijo}-borrador-estado`), estado),
  });

  const borrador = conectarBorradorDeFormulario({
    contenedor: modal,
    modal,
    gestor,
    leerDatos,
    aplicarDatos,
  });

  const aviso = modal.querySelector(`#${sufijo}-borrador-aviso`);
  if (borrador.restaurado && aviso) aviso.hidden = false;

  const btnDescartar = modal.querySelector(`#${sufijo}-borrador-descartar`);
  if (btnDescartar) {
    btnDescartar.addEventListener('click', () => {
      borrador.descartar();
      if (aviso) aviso.hidden = true;
      aplicarEstadoBorrador(modal.querySelector(`#${sufijo}-borrador-estado`), null);
    });
  }

  return borrador;
}

/** Marca de aviso + estado del borrador, reutilizable en los modales. */
function htmlAvisoBorrador(sufijo) {
  return `
    <p class="pf-texto-suave" id="${sufijo}-borrador-aviso" hidden style="margin-top: 0;">
      Recuperamos un borrador sin guardar.
      <button type="button" class="pf-boton pf-boton--secundario pf-boton--chico" id="${sufijo}-borrador-descartar">Descartar</button>
    </p>
  `;
}

function htmlEstadoBorrador(sufijo, alineadoAIzquierda = false) {
  const estilo = alineadoAIzquierda ? 'margin-right:auto;' : '';
  return `<span class="pf-texto-suave" id="${sufijo}-borrador-estado" style="font-size: 12px; ${estilo}"></span>`;
}

export async function vistaVariantes(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const empresa = obtenerEstadoSesion().empresa ?? {};
  const tieneVariantes = tieneFeature(empresa, 'variantes');
  const productoIdFiltro = leerQueryParam('producto_id');

  const contenido = montarShell(contenedorRaiz, '/variantes');
  contenido.innerHTML = `
    <div class="pf-fila pf-fila--separada" style="margin-bottom: 16px;">
      <div>
        <h1 style="margin: 0;">Características y Variantes</h1>
        <p class="pf-texto-suave" style="margin: 4px 0 0;">
          Primero crea las características que pueden cambiar entre productos, por ejemplo Color, Talla o Material.
        </p>
      </div>
      ${
        !tieneVariantes
          ? `
        <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-upgrade-variantes">
          Mejorar plan
        </button>
      `
          : `
        <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-atributo">
          + Nueva característica
        </button>
      `
      }
    </div>

    ${
      !tieneVariantes
        ? `
      <div class="pf-campo__error" style="padding: 16px; margin-bottom: 16px; display: flex; align-items: flex-start; gap: 12px;">
        <span style="color: var(--pf-color-premium); flex-shrink: 0; margin-top: 2px;">${iconoCandado()}</span>
        <div>
          <strong>Variantes</strong> — función bloqueada<br>
          Disponible desde el plan <strong>Medio</strong>.<br>
          Permite crear tallas, colores y combinaciones diferentes para cada producto.<br>
          <button type="button" class="pf-boton pf-boton--premium" id="pf-btn-ver-planes" style="margin-top: 12px;">
            Mejorar plan
          </button>
        </div>
      </div>
    `
        : ''
    }

    <div id="pf-variantes-atributos"></div>
    <div id="pf-variantes-producto" style="margin-top: 32px;"></div>

    <pf-modal titulo="Nuevo atributo" id="pf-modal-atributo"></pf-modal>
    <pf-modal titulo="Valores" id="pf-modal-valores"></pf-modal>
    <pf-modal titulo="Editar valor" id="pf-modal-editar-valor"></pf-modal>
    <pf-modal titulo="Nueva variante" id="pf-modal-variante"></pf-modal>
  `;

  if (!tieneVariantes) {
    const btnUpgrade = contenido.querySelector('#pf-btn-upgrade-variantes');
    const btnVerPlanes = contenido.querySelector('#pf-btn-ver-planes');
    if (btnUpgrade) {
      btnUpgrade.addEventListener('click', () => {
        window.location.hash = '#/planes';
      });
    }
    if (btnVerPlanes) {
      btnVerPlanes.addEventListener('click', () => {
        window.location.hash = '#/planes';
      });
    }
    return;
  }

  await cargarAtributos(contenido);

  if (productoIdFiltro) {
    await cargarVariantesProducto(contenido, productoIdFiltro);
  }
}

async function cargarAtributos(contenedor) {
  const destino = document.getElementById('pf-variantes-atributos');
  if (!destino) return;

  destino.innerHTML = '<p class="pf-texto-suave">Cargando características…</p>';

  const { data: atributos, error } = await listarAtributos();

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">${mensajeErrorAmigable(error)}</p>`;
    return;
  }

  if (!atributos || atributos.length === 0) {
    destino.innerHTML = `
      <p class="pf-texto-suave">No hay características creadas todavía.</p>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-nuevo-atributo-empty" style="margin-top: 8px;">
        + Crear primera característica
      </button>
    `;
    const btnNuevo = destino.querySelector('#pf-btn-nuevo-atributo-empty');
    if (btnNuevo) btnNuevo.addEventListener('click', () => abrirModalAtributo(contenedor));
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay características.',
    columnas: [
      { titulo: 'Característica', render: (fila) => fila.nombre },
      {
        titulo: 'Visualización',
        render: (fila) => {
          const tipo =
            {
              texto: 'Texto',
              color: 'Color',
              imagen: 'Imagen',
            }[fila.tipo_visualizacion] ?? fila.tipo_visualizacion;
          return tipo;
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => {
          const btnEditar = document.createElement('button');
          btnEditar.type = 'button';
          btnEditar.className = 'pf-boton pf-boton--secundario';
          btnEditar.textContent = 'Editar';
          btnEditar.addEventListener('click', () => abrirModalAtributo(contenedor, fila));

          const btnValores = document.createElement('button');
          btnValores.type = 'button';
          btnValores.className = 'pf-boton pf-boton--secundario';
          btnValores.textContent = 'Valores';
          btnValores.addEventListener('click', () => abrirModalValores(contenedor, fila));

          const btnEliminar = document.createElement('button');
          btnEliminar.type = 'button';
          btnEliminar.className = 'pf-boton pf-boton--peligro';
          btnEliminar.textContent = 'Eliminar';
          btnEliminar.addEventListener('click', async () => {
            if (
              !confirm(
                '¿Eliminar esta característica? Se eliminarán sus valores y variantes asociadas.',
              )
            )
              return;
            const { error } = await eliminarAtributo(fila.id);
            if (error) {
              mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
              return;
            }
            mostrarToast('Característica eliminada.', { tono: 'exito' });
            cargarAtributos(contenedor);
          });

          const wrapper = document.createElement('div');
          wrapper.style.display = 'flex';
          wrapper.style.gap = '4px';
          wrapper.appendChild(btnEditar);
          wrapper.appendChild(btnValores);
          wrapper.appendChild(btnEliminar);
          return wrapper;
        },
      },
    ],
    filas: atributos,
  });

  destino.innerHTML = '';
  destino.appendChild(tabla);

  const btnNuevo = document.getElementById('pf-btn-nuevo-atributo');
  if (btnNuevo) {
    btnNuevo.addEventListener('click', () => abrirModalAtributo(contenedor));
  }
}

function abrirModalAtributo(contenedor, atributo = null) {
  const esEdicion = !!atributo;
  const modal =
    document.getElementById('pf-modal-atributo') ?? contenedor?.querySelector('#pf-modal-atributo');
  modal.setAttribute('titulo', esEdicion ? 'Editar característica' : 'Nueva característica');
  modal.innerHTML = `
    ${htmlAvisoBorrador('pf-atributo')}
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-atributo-nombre">Nombre de la característica</label>
      <input type="text" id="pf-atributo-nombre" value="${escapeAttr(esEdicion ? atributo.nombre : '')}" placeholder="Ej: Color, Talla, Material" />
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-atributo-tipo">Tipo de visualización</label>
      <select id="pf-atributo-tipo">
        <option value="texto" ${esEdicion && atributo.tipo_visualizacion === 'texto' ? 'selected' : ''}>Texto</option>
        <option value="color" ${esEdicion && atributo.tipo_visualizacion === 'color' ? 'selected' : ''}>Color</option>
        <option value="imagen" ${esEdicion && atributo.tipo_visualizacion === 'imagen' ? 'selected' : ''}>Imagen</option>
      </select>
      <p class="pf-texto-suave" style="margin-top: 4px; font-size: 12px;">Elige cómo mostrar las opciones al cliente.</p>
    </div>
    <div style="display: flex; gap: 8px; justify-content: flex-end; align-items: center; margin-top: 16px;">
      ${htmlEstadoBorrador('pf-atributo', true)}
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-cancelar-atributo">Cancelar</button>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-guardar-atributo">Guardar</button>
    </div>
  `;
  modal.abrir();

  const inputNombre = modal.querySelector('#pf-atributo-nombre');
  const inputTipo = modal.querySelector('#pf-atributo-tipo');

  const borrador = conectarBorradorModal({
    modal,
    ambito: 'formulario-atributo',
    contexto: esEdicion ? `editar:${atributo.id}` : 'nuevo',
    leerDatos: () => ({ nombre: inputNombre.value, tipo_visualizacion: inputTipo.value }),
    aplicarDatos: (datos) => {
      if (datos.nombre !== undefined && datos.nombre !== null) inputNombre.value = datos.nombre;
      if (datos.tipo_visualizacion) inputTipo.value = datos.tipo_visualizacion;
    },
    sufijo: 'pf-atributo',
  });

  const btnCancelar = modal.querySelector('#pf-btn-cancelar-atributo');
  const btnGuardar = modal.querySelector('#pf-btn-guardar-atributo');

  const cerrar = () => modal.cerrar();
  btnCancelar.addEventListener('click', cerrar);

  btnGuardar.addEventListener('click', async () => {
    const nombre = inputNombre.value.trim();
    const tipo_visualizacion = inputTipo.value;

    if (!nombre) {
      mostrarToast('El nombre es obligatorio.', { tono: 'peligro' });
      return;
    }

    const datos = { nombre, tipo_visualizacion };

    const resultado = esEdicion
      ? await actualizarAtributo(atributo.id, datos)
      : await crearAtributo(datos);
    const { error } = resultado;

    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
      return;
    }

    mostrarToast(esEdicion ? 'Característica actualizada.' : 'Característica creada.', {
      tono: 'exito',
    });
    borrador.finalizar();
    cerrar();
    cargarAtributos(contenedor);
  });
}

async function abrirModalValores(contenedor, atributo) {
  const modal = contenedor.querySelector('#pf-modal-valores');
  modal.setAttribute('titulo', `Valores de ${atributo.nombre}`);
  const esColor = atributo.tipo_visualizacion === 'color';
  const esImagen = atributo.tipo_visualizacion === 'imagen';

  modal.innerHTML = `
    ${htmlAvisoBorrador('pf-valor')}
    <p class="pf-texto-suave" style="margin-top: 0; margin-bottom: 16px;">
      Agrega las opciones disponibles para esta característica.
    </p>
    <div id="pf-valores-lista"></div>
    <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--pf-color-borde, #eee);">
      <h3 style="margin-top: 0;">Agregar nueva opción</h3>
      <div class="pf-campo" style="margin-bottom: 8px;">
        <label for="pf-valor-texto">Nombre de la opción</label>
        <input type="text" id="pf-valor-texto" placeholder="${esColor ? 'Ej: Rojo, Azul' : esImagen ? 'Ej: Estampado Floral' : 'Ej: S, M, L'}" />
      </div>
      ${
        esColor
          ? `
        <div class="pf-campo" style="margin-bottom: 8px;">
          <label for="pf-valor-color">Muestra de Color</label>
          <input type="color" id="pf-valor-color" value="#000000" style="height: 40px; cursor: pointer;" />
        </div>
      `
          : ''
      }
      ${
        esImagen
          ? `
        <div class="pf-campo" style="margin-bottom: 8px;">
          <label for="pf-valor-imagen">URL de la imagen de muestra (opcional)</label>
          <input type="url" id="pf-valor-imagen" placeholder="https://..." />
        </div>
      `
          : ''
      }
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-crear-valor" style="margin-top: 8px;">
        + Agregar opción
      </button>
      ${htmlEstadoBorrador('pf-valor')}
    </div>
  `;
  modal.abrir();

  await cargarValoresEnModal(modal, atributo);

  const borrador = conectarBorradorModal({
    modal,
    ambito: 'formulario-valor',
    contexto: `nuevo:${atributo.id}`,
    leerDatos: () => ({
      valor: modal.querySelector('#pf-valor-texto')?.value ?? '',
      valor_color_hex: modal.querySelector('#pf-valor-color')?.value ?? null,
      valor_imagen_url: modal.querySelector('#pf-valor-imagen')?.value ?? '',
    }),
    aplicarDatos: (datos) => {
      const texto = modal.querySelector('#pf-valor-texto');
      const color = modal.querySelector('#pf-valor-color');
      const imagen = modal.querySelector('#pf-valor-imagen');
      if (texto && datos.valor !== undefined && datos.valor !== null) texto.value = datos.valor;
      if (color && datos.valor_color_hex) color.value = datos.valor_color_hex;
      if (imagen && datos.valor_imagen_url !== undefined && datos.valor_imagen_url !== null) {
        imagen.value = datos.valor_imagen_url;
      }
    },
    sufijo: 'pf-valor',
  });

  const btnCrear = modal.querySelector('#pf-btn-crear-valor');
  if (btnCrear) {
    btnCrear.addEventListener('click', async () => {
      const valor = modal.querySelector('#pf-valor-texto').value.trim();
      const valor_color_hex = esColor ? modal.querySelector('#pf-valor-color').value : null;
      const valor_imagen_url = esImagen
        ? modal.querySelector('#pf-valor-imagen').value.trim() || null
        : null;

      if (!valor) {
        mostrarToast('El nombre de la opción es obligatorio.', { tono: 'peligro' });
        return;
      }

      const { data: valoresActuales } = await listarValores(atributo.id);
      const orden = valoresActuales?.length ?? 0;

      const { error } = await crearValor(atributo.id, {
        valor,
        valor_color_hex,
        valor_imagen_url,
        orden,
      });
      if (error) {
        mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
        return;
      }

      mostrarToast('Opción creada.', { tono: 'exito' });
      borrador.descartar();
      modal.querySelector('#pf-valor-texto').value = '';
      if (esImagen && modal.querySelector('#pf-valor-imagen')) {
        modal.querySelector('#pf-valor-imagen').value = '';
      }
      await cargarValoresEnModal(modal, atributo);
    });
  }
}

async function cargarValoresEnModal(modal, atributo) {
  const lista = modal.querySelector('#pf-valores-lista');
  if (!lista) return;

  const { data: valores, error } = await listarValores(atributo.id);
  if (error) {
    lista.innerHTML = `<p class="pf-campo__error">${mensajeErrorAmigable(error)}</p>`;
    return;
  }

  if (!valores || valores.length === 0) {
    lista.innerHTML = '<p class="pf-texto-suave">No hay opciones creadas aún.</p>';
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay opciones.',
    columnas: [
      { titulo: 'Opción / Valor', render: (fila) => fila.valor },
      {
        titulo: 'Muestra',
        render: (fila) => {
          if (fila.valor_color_hex) {
            return `<span style="display:inline-block;width:24px;height:24px;background:${fila.valor_color_hex};border-radius:4px;border:1px solid #ccc;vertical-align:middle;"></span>`;
          }
          if (fila.valor_imagen_url) {
            return `<img src="${fila.valor_imagen_url}" alt="" style="width:24px;height:24px;object-fit:cover;border-radius:4px;border:1px solid #ccc;vertical-align:middle;" />`;
          }
          return '—';
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => {
          const btnEditar = document.createElement('button');
          btnEditar.type = 'button';
          btnEditar.className = 'pf-boton pf-boton--secundario';
          btnEditar.textContent = 'Editar';
          btnEditar.addEventListener('click', () => abrirModalEditarValor(modal, fila, atributo));

          const btnEliminar = document.createElement('button');
          btnEliminar.type = 'button';
          btnEliminar.className = 'pf-boton pf-boton--peligro';
          btnEliminar.textContent = 'Eliminar';
          btnEliminar.addEventListener('click', async () => {
            if (!confirm('¿Eliminar esta opción?')) return;
            const { error } = await eliminarValor(fila.id);
            if (error) {
              mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
              return;
            }
            mostrarToast('Opción eliminada.', { tono: 'exito' });
            await cargarValoresEnModal(modal, atributo);
          });

          const wrapper = document.createElement('div');
          wrapper.style.display = 'flex';
          wrapper.style.gap = '4px';
          wrapper.appendChild(btnEditar);
          wrapper.appendChild(btnEliminar);
          return wrapper;
        },
      },
    ],
    filas: valores,
  });

  lista.innerHTML = '';
  lista.appendChild(tabla);
}

function abrirModalEditarValor(modalPadre, valor, atributo) {
  const modal = modalPadre.querySelector('#pf-modal-editar-valor') ?? modalPadre;
  if (modal === modalPadre) {
    modal.setAttribute('titulo', 'Editar opción');
  }
  const esColor = atributo.tipo_visualizacion === 'color';
  const esImagen = atributo.tipo_visualizacion === 'imagen';

  modal.innerHTML = `
    ${htmlAvisoBorrador('pf-valor-editar')}
    <div class="pf-campo" style="margin-bottom: 8px;">
      <label for="pf-valor-edit-texto">Nombre de la opción</label>
      <input type="text" id="pf-valor-edit-texto" value="${escapeAttr(valor.valor)}" />
    </div>
    ${
      esColor
        ? `
      <div class="pf-campo" style="margin-bottom: 8px;">
        <label for="pf-valor-edit-color">Muestra de Color</label>
        <input type="color" id="pf-valor-edit-color" value="${valor.valor_color_hex || '#000000'}" style="height: 40px; cursor: pointer;" />
      </div>
    `
        : ''
    }
    ${
      esImagen
        ? `
      <div class="pf-campo" style="margin-bottom: 8px;">
        <label for="pf-valor-edit-imagen">URL de la imagen (opcional)</label>
        <input type="url" id="pf-valor-edit-imagen" value="${escapeAttr(valor.valor_imagen_url || '')}" placeholder="https://..." />
      </div>
    `
        : ''
    }
    <div style="display: flex; gap: 8px; justify-content: flex-end; align-items: center; margin-top: 16px;">
      ${htmlEstadoBorrador('pf-valor-editar', true)}
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-cancelar-editar-valor">Cancelar</button>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-guardar-editar-valor">Guardar</button>
    </div>
  `;
  if (modal !== modalPadre) {
    modal.abrir();
  }

  const borrador = conectarBorradorModal({
    modal,
    ambito: 'formulario-valor',
    contexto: `editar:${valor.id}`,
    leerDatos: () => ({
      valor: modal.querySelector('#pf-valor-edit-texto')?.value ?? '',
      valor_color_hex: modal.querySelector('#pf-valor-edit-color')?.value ?? null,
      valor_imagen_url: modal.querySelector('#pf-valor-edit-imagen')?.value ?? '',
    }),
    aplicarDatos: (datos) => {
      const texto = modal.querySelector('#pf-valor-edit-texto');
      const color = modal.querySelector('#pf-valor-edit-color');
      const imagen = modal.querySelector('#pf-valor-edit-imagen');
      if (texto && datos.valor !== undefined && datos.valor !== null) texto.value = datos.valor;
      if (color && datos.valor_color_hex) color.value = datos.valor_color_hex;
      if (imagen && datos.valor_imagen_url !== undefined && datos.valor_imagen_url !== null) {
        imagen.value = datos.valor_imagen_url;
      }
    },
    sufijo: 'pf-valor-editar',
  });

  const btnCancelar = modal.querySelector('#pf-btn-cancelar-editar-valor');
  const btnGuardar = modal.querySelector('#pf-btn-guardar-editar-valor');

  btnCancelar.addEventListener('click', () => {
    if (modal !== modalPadre) {
      modal.cerrar();
    }
  });
  btnGuardar.addEventListener('click', async () => {
    const nuevoValor = modal.querySelector('#pf-valor-edit-texto').value.trim();
    const nuevoColor = esColor ? modal.querySelector('#pf-valor-edit-color').value : null;
    const nuevaImagen = esImagen
      ? modal.querySelector('#pf-valor-edit-imagen').value.trim() || null
      : null;

    if (!nuevoValor) {
      mostrarToast('El nombre es obligatorio.', { tono: 'peligro' });
      return;
    }

    const { error } = await actualizarValor(valor.id, {
      valor: nuevoValor,
      valor_color_hex: nuevoColor,
      valor_imagen_url: nuevaImagen,
    });

    if (error) {
      mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
      return;
    }

    mostrarToast('Opción actualizada.', { tono: 'exito' });
    borrador.finalizar();
    if (modal !== modalPadre) {
      modal.cerrar();
    }
    await cargarValoresEnModal(modalPadre, atributo);
  });
}

// ============================================================================
// Variantes por producto
// ============================================================================

async function cargarVariantesProducto(contenedor, productoId) {
  const destino = contenedor.querySelector('#pf-variantes-producto');
  if (!destino) return;
  destino.innerHTML = '<p class="pf-texto-suave">Cargando variantes del producto…</p>';

  const [{ data: variantes, error }, { data: producto }] = await Promise.all([
    listarVariantesProducto(productoId),
    obtenerProducto(productoId),
  ]);

  if (error) {
    destino.innerHTML = `<p class="pf-campo__error">${mensajeErrorAmigable(error)}</p>`;
    return;
  }

  destino.innerHTML = `
    <div style="border-top: 1px solid var(--pf-color-borde, #eee); padding-top: 24px; margin-top: 24px;">
      <h2 style="margin-top: 0;">Variantes de producto</h2>
      <p style="margin-top: 4px; font-weight: 600;">Producto: ${producto ? producto.nombre : productoId}</p>

      <div class="pf-tarjeta" style="background: var(--pf-color-fondo-alterno, #f9fafb); padding: 16px; border-radius: 8px; border: 1px solid var(--pf-color-borde, #e5e7eb); margin-bottom: 24px;">
        <h3 style="margin-top: 0; margin-bottom: 8px;">Generar combinaciones automáticamente</h3>
        <p class="pf-texto-suave" style="margin-top: 0; margin-bottom: 12px;">
          Selecciona las características que tendrá este producto.
        </p>
        <div id="pf-generador-caracteristicas"><p class="pf-texto-suave">Cargando opciones…</p></div>
        <div style="margin-top: 16px;">
          <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-generar-combos">
            ⚡ Generar combinaciones
          </button>
          <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-nueva-variante-manual" style="margin-left: 8px;">
            + Nueva variante manual
          </button>
        </div>
      </div>

      <div id="pf-tabla-variantes-container"></div>
    </div>
  `;

  const generadorContenedor = destino.querySelector('#pf-generador-caracteristicas');
  const { data: atributos } = await listarAtributos();

  if (!atributos || atributos.length === 0) {
    generadorContenedor.innerHTML =
      '<p class="pf-texto-suave">Primero crea características arriba (ej: Color, Talla) para generar combinaciones.</p>';
  } else {
    let htmlGrupos = '';
    for (const attr of atributos) {
      const { data: valores } = await listarValores(attr.id);
      if (!valores || valores.length === 0) continue;

      htmlGrupos += `
        <div class="pf-campo" style="margin-bottom: 12px;">
          <strong style="display: block; margin-bottom: 6px;">${attr.nombre}</strong>
          <div style="display: flex; flex-wrap: wrap; gap: 12px;">
            ${valores
              .map(
                (v) => `
              <label style="display: inline-flex; align-items: center; gap: 6px; cursor: pointer;">
                <input type="checkbox" class="pf-combo-val" data-attr-id="${attr.id}" data-val-id="${v.id}" data-val-nombre="${v.valor}" />
                <span>${v.valor}</span>
              </label>
            `,
              )
              .join('')}
          </div>
        </div>
      `;
    }
    generadorContenedor.innerHTML =
      htmlGrupos ||
      '<p class="pf-texto-suave">No hay opciones disponibles en tus características.</p>';
  }

  const btnGenerar = destino.querySelector('#pf-btn-generar-combos');
  if (btnGenerar) {
    btnGenerar.addEventListener('click', async () => {
      if (!atributos || atributos.length === 0) {
        mostrarToast('Crea características primero.', { tono: 'peligro' });
        return;
      }

      const seleccion = [];
      for (const attr of atributos) {
        const cbs = generadorContenedor.querySelectorAll(
          `input[data-attr-id="${attr.id}"]:checked`,
        );
        if (cbs.length > 0) {
          const vals = Array.from(cbs).map((cb) => ({
            id: cb.dataset.valId,
            valor: cb.dataset.valNombre,
          }));
          seleccion.push({ atributo: attr.nombre, valores: vals });
        }
      }

      if (seleccion.length === 0) {
        mostrarToast('Selecciona al menos una opción.', { tono: 'peligro' });
        return;
      }

      btnGenerar.disabled = true;
      btnGenerar.textContent = 'Generando…';

      const {
        creadas,
        omitidas,
        error: errGen,
      } = await generarCombinacionesVariantes(productoId, seleccion, producto?.slug);

      btnGenerar.disabled = false;
      btnGenerar.textContent = '⚡ Generar combinaciones';

      if (errGen) {
        mostrarToast(mensajeErrorAmigable(errGen), { tono: 'peligro' });
        return;
      }

      if (creadas > 0) {
        mostrarToast(
          `Se generaron ${creadas} combinaciones.${omitidas > 0 ? ` (${omitidas} ya existían)` : ''}`,
          {
            tono: 'exito',
          },
        );
      } else if (omitidas > 0) {
        mostrarToast('Las combinaciones seleccionadas ya existían.', { tono: 'neutral' });
      } else {
        mostrarToast('No se generaron variantes.', { tono: 'neutral' });
      }

      await cargarVariantesProducto(contenedor, productoId);
    });
  }

  const btnManual = destino.querySelector('#pf-btn-nueva-variante-manual');
  if (btnManual) {
    btnManual.addEventListener('click', () => abrirModalVariante(contenedor, productoId));
  }

  const tablaContainer = destino.querySelector('#pf-tabla-variantes-container');

  if (!variantes || variantes.length === 0) {
    tablaContainer.innerHTML =
      '<p class="pf-texto-suave">No hay variantes creadas para este producto.</p>';
    return;
  }

  const tabla = crearTabla({
    filaVacia: 'No hay variantes.',
    columnas: [
      {
        titulo: 'Combinación',
        render: (fila) => {
          if (!fila.valores || fila.valores.length === 0) return '—';
          return fila.valores
            .map(
              (v) =>
                `${v.atributo_valor?.atributo?.nombre ?? ''}: ${v.atributo_valor?.valor ?? ''}`,
            )
            .join(' / ');
        },
      },
      { titulo: 'SKU', render: (fila) => fila.sku ?? '—' },
      {
        titulo: 'Precio',
        render: (fila) =>
          fila.precio !== null && fila.precio !== undefined
            ? `S/ ${Number(fila.precio).toFixed(2)}`
            : '—',
      },
      { titulo: 'Stock', render: (fila) => fila.stock ?? 0 },
      {
        titulo: 'Estado',
        render: (fila) => {
          const badge = document.createElement('pf-badge-estado');
          if (fila.estado === 'oculta') {
            badge.setAttribute('tono', 'alerta');
            badge.textContent = 'No disponible';
          } else if (fila.stock === 0) {
            badge.setAttribute('tono', 'advertencia');
            badge.textContent = 'Agotada';
          } else {
            badge.setAttribute('tono', 'exito');
            badge.textContent = 'Disponible';
          }
          return badge;
        },
      },
      {
        titulo: 'Acciones',
        render: (fila) => {
          const btnEditar = document.createElement('button');
          btnEditar.type = 'button';
          btnEditar.className = 'pf-boton pf-boton--secundario';
          btnEditar.textContent = 'Editar';
          btnEditar.addEventListener('click', () =>
            abrirModalVariante(contenedor, productoId, fila),
          );

          const btnEliminar = document.createElement('button');
          btnEliminar.type = 'button';
          btnEliminar.className = 'pf-boton pf-boton--peligro';
          btnEliminar.textContent = 'Eliminar';
          btnEliminar.addEventListener('click', async () => {
            if (!confirm('¿Eliminar esta variante?')) return;
            const { error } = await eliminarVariante(fila.id);
            if (error) {
              mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
              return;
            }
            mostrarToast('Variante eliminada.', { tono: 'exito' });
            await cargarVariantesProducto(contenedor, productoId);
          });

          const wrapper = document.createElement('div');
          wrapper.style.display = 'flex';
          wrapper.style.gap = '4px';
          wrapper.appendChild(btnEditar);
          wrapper.appendChild(btnEliminar);
          return wrapper;
        },
      },
    ],
    filas: variantes,
  });

  tablaContainer.appendChild(tabla);
}

async function abrirModalVariante(contenedor, productoId, variante = null) {
  const esEdicion = !!variante;
  const modal = contenedor.querySelector('#pf-modal-variante');
  modal.setAttribute('titulo', esEdicion ? 'Editar variante' : 'Nueva variante');

  const { data: atributos, error } = await listarAtributos();
  if (error) {
    mostrarToast('No se pudieron cargar las características.', { tono: 'peligro' });
    return;
  }

  const valoresSeleccionados = new Set(
    (variante?.valores ?? []).filter((v) => v.atributo_valor).map((v) => v.atributo_valor.id),
  );

  modal.innerHTML = `
    ${htmlAvisoBorrador('pf-variante')}
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label>Características</label>
      <div id="pf-variante-atributos">
        ${
          (atributos ?? [])
            .map(
              (attr) => `
          <div class="pf-campo" style="margin-bottom: 8px;">
            <label>${attr.nombre}</label>
            <select class="pf-variante-atributo-select" data-atributo-id="${attr.id}">
              <option value="">— Seleccionar —</option>
            </select>
          </div>
        `,
            )
            .join('') || '<p class="pf-texto-suave">Crea características primero.</p>'
        }
      </div>
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-variante-sku">SKU</label>
      <input type="text" id="pf-variante-sku" value="${esEdicion ? (variante.sku ?? '') : ''}" placeholder="Ej: CAM-ROS-S" />
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-variante-precio">Precio (S/)</label>
      <input type="number" id="pf-variante-precio" min="0" step="0.01" value="${esEdicion ? (variante.precio ?? '') : ''}" placeholder="Opcional" />
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-variante-stock">Stock</label>
      <input type="number" id="pf-variante-stock" min="0" value="${esEdicion ? (variante.stock ?? 0) : 0}" />
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-variante-imagen">Imagen URL</label>
      <input type="text" id="pf-variante-imagen" value="${esEdicion ? (variante.imagen_url ?? '') : ''}" placeholder="https://..." />
    </div>
    <div class="pf-campo" style="margin-bottom: 12px;">
      <label for="pf-variante-estado">Estado</label>
      <select id="pf-variante-estado">
        <option value="visible" ${!esEdicion || variante.estado === 'visible' ? 'selected' : ''}>Disponible / Visibilidad pública</option>
        <option value="oculta" ${esEdicion && variante.estado === 'oculta' ? 'selected' : ''}>Oculta / No disponible</option>
      </select>
    </div>
    <div style="display: flex; gap: 8px; justify-content: flex-end; align-items: center;">
      ${htmlEstadoBorrador('pf-variante', true)}
      <button type="button" class="pf-boton pf-boton--secundario" id="pf-btn-cancelar-variante">Cancelar</button>
      <button type="button" class="pf-boton pf-boton--primario" id="pf-btn-guardar-variante">Guardar</button>
    </div>
  `;
  modal.abrir();

  for (const attr of atributos ?? []) {
    const select = modal.querySelector(`select[data-atributo-id="${attr.id}"]`);
    if (!select) continue;
    const { data: valores } = await listarValores(attr.id);
    for (const val of valores ?? []) {
      const option = document.createElement('option');
      option.value = val.id;
      option.textContent = val.valor;
      if (valoresSeleccionados.has(val.id)) option.selected = true;
      select.appendChild(option);
    }
  }

  // Después de poblar los selects (el borrador guarda el valor elegido por id).
  const borrador = conectarBorradorModal({
    modal,
    ambito: 'formulario-variante',
    contexto: esEdicion ? `editar:${variante.id}` : `nuevo:${productoId}`,
    leerDatos: () => leerDatosFormularioVariante(modal),
    aplicarDatos: (datos) => aplicarDatosFormularioVariante(modal, datos),
    sufijo: 'pf-variante',
  });

  const btnCancelar = modal.querySelector('#pf-btn-cancelar-variante');
  const btnGuardar = modal.querySelector('#pf-btn-guardar-variante');

  btnCancelar.addEventListener('click', () => modal.cerrar());
  btnGuardar.addEventListener('click', async () => {
    const selects = modal.querySelectorAll('.pf-variante-atributo-select');
    const valoresSeleccionados = Array.from(selects)
      .map((s) => s.value)
      .filter((v) => v);

    if (valoresSeleccionados.length === 0) {
      mostrarToast('Selecciona al menos una opción.', { tono: 'peligro' });
      return;
    }

    const sku = modal.querySelector('#pf-variante-sku').value.trim() || null;
    const precioRaw = modal.querySelector('#pf-variante-precio').value;
    const precio = precioRaw !== '' ? parseFloat(precioRaw) : null;
    const stock = parseInt(modal.querySelector('#pf-variante-stock').value, 10) || 0;
    const imagen_url = modal.querySelector('#pf-variante-imagen').value.trim() || null;
    const estado = modal.querySelector('#pf-variante-estado').value;

    if (precio !== null && precio < 0) {
      mostrarToast('El precio no puede ser negativo.', { tono: 'peligro' });
      return;
    }

    const datos = { sku, precio, stock, imagen_url, estado };

    let varianteId;
    if (esEdicion) {
      const { error } = await actualizarVariante(variante.id, datos);
      if (error) {
        mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
        return;
      }
      varianteId = variante.id;
      await eliminarValoresVariante(varianteId);
    } else {
      const { data: nueva, error } = await crearVariante(productoId, datos);
      if (error) {
        mostrarToast(mensajeErrorAmigable(error), { tono: 'peligro' });
        return;
      }
      varianteId = nueva.id;
    }

    const { error: errorValores } = await asignarValoresVariante(varianteId, valoresSeleccionados);
    if (errorValores) {
      mostrarToast(mensajeErrorAmigable(errorValores), { tono: 'peligro' });
      return;
    }

    mostrarToast(esEdicion ? 'Variante actualizada.' : 'Variante creada.', { tono: 'exito' });
    borrador.finalizar();
    modal.cerrar();
    await cargarVariantesProducto(contenedor, productoId);
  });
}

/** Campos del modal de variante que se persisten como borrador. */
function leerDatosFormularioVariante(modal) {
  const atributos = {};
  for (const select of modal.querySelectorAll('.pf-variante-atributo-select')) {
    atributos[select.dataset.atributoId] = select.value;
  }
  return {
    atributos,
    sku: modal.querySelector('#pf-variante-sku')?.value ?? '',
    precio: modal.querySelector('#pf-variante-precio')?.value ?? '',
    stock: modal.querySelector('#pf-variante-stock')?.value ?? '',
    imagen_url: modal.querySelector('#pf-variante-imagen')?.value ?? '',
    estado: modal.querySelector('#pf-variante-estado')?.value ?? '',
  };
}

function aplicarDatosFormularioVariante(modal, datos) {
  const setValor = (selector, valorDato) => {
    const el = modal.querySelector(selector);
    if (el && valorDato !== undefined && valorDato !== null) el.value = valorDato;
  };
  setValor('#pf-variante-sku', datos.sku);
  setValor('#pf-variante-precio', datos.precio);
  setValor('#pf-variante-stock', datos.stock);
  setValor('#pf-variante-imagen', datos.imagen_url);
  setValor('#pf-variante-estado', datos.estado);

  if (datos.atributos && typeof datos.atributos === 'object') {
    for (const select of modal.querySelectorAll('.pf-variante-atributo-select')) {
      const valorDato = datos.atributos[select.dataset.atributoId];
      if (valorDato !== undefined && valorDato !== null) select.value = valorDato;
    }
  }
}
